/**
 * The API client.
 *
 * Every failure the network can produce is translated into one of a small set
 * of error kinds, so the UI can show a sentence the user can act on instead of
 * a status code. The widget never displays a raw server message.
 */

import { ChatResponse, PageContext, RemoteConfig, ScreenContext, SessionResponse } from './types';

export type ApiErrorKind =
    | 'offline'
    | 'timeout'
    | 'rate-limited'
    | 'unavailable'
    | 'not-found'
    | 'invalid'
    | 'unknown';

export class ApiError extends Error {
    constructor(
        public readonly kind: ApiErrorKind,
        message: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

const REQUEST_TIMEOUT_MS = 30000;

export class ChatbotApi {
    private readonly baseUrl: string;

    constructor(apiUrl: string) {
        this.baseUrl = apiUrl.replace(/\/+$/, '');
    }

    public async getConfig(appId: string, language: string): Promise<RemoteConfig> {
        return this.request<RemoteConfig>(
            'GET',
            `/v1/chat/config/${encodeURIComponent(appId)}?language=${encodeURIComponent(language)}`
        );
    }

    public async createSession(
        appId: string,
        language: string,
        context: { page?: PageContext; screen?: ScreenContext } | undefined
    ): Promise<SessionResponse> {
        return this.request<SessionResponse>('POST', '/v1/chat/session', {
            appId,
            language,
            context
        });
    }

    public async sendMessage(payload: {
        sessionId?: string;
        appId: string;
        message: string;
        language: string;
        context?: { page?: PageContext; screen?: ScreenContext };
    }): Promise<ChatResponse> {
        return this.request<ChatResponse>('POST', '/v1/chat/message', payload);
    }

    private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        let response: Response;
        try {
            response = await fetch(`${this.baseUrl}${path}`, {
                method,
                headers:
                    body === undefined ? {} : { 'content-type': 'application/json' },
                body: body === undefined ? undefined : JSON.stringify(body),
                signal: controller.signal,
                // No cookies are used, and sending them would force the server
                // into a credentialed CORS mode it deliberately does not allow.
                credentials: 'omit',
                mode: 'cors'
            });
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                throw new ApiError('timeout', 'request timed out');
            }
            throw new ApiError('offline', 'network request failed');
        } finally {
            clearTimeout(timer);
        }

        if (!response.ok) {
            throw new ApiError(
                this.classify(response.status),
                `http ${response.status}`
            );
        }

        try {
            return (await response.json()) as T;
        } catch {
            throw new ApiError('invalid', 'response was not valid JSON');
        }
    }

    private classify(status: number): ApiErrorKind {
        if (status === 429) {
            return 'rate-limited';
        }
        if (status === 404) {
            return 'not-found';
        }
        if (status === 400 || status === 413) {
            return 'invalid';
        }
        if (status >= 500) {
            return 'unavailable';
        }
        return 'unknown';
    }
}
